import { useState } from "react";
import { CheckCircle2, ArrowRight, Award, GraduationCap } from "lucide-react";
import SEO from "../components/SEO";
import AppointmentDialog from "../components/AppointmentDialog";
import { ASSETS, DOCTORS, SITE } from "../lib/constants";
import useReveal from "../hooks/useReveal";

export default function About() {
  useReveal();
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <SEO
        title="About Our Dentists | Dr. Rahul & Dr. Monika Tiwari - Lotus Family Dental Allahabad"
        description="Meet Dr. Rahul Tiwari (MDS Orthodontist, Certified Osstem Implantologist) and Dr. Monika Tiwari (MDS Gold Medalist, Pedodontist) — Allahabad's most trusted dental specialists."
        keywords="best dentist allahabad, Dr Rahul Tiwari, Dr Monika Tiwari, MDS dentist prayagraj"
        canonical="https://lotusfamilydental.me/about"
      />

      {/* Hero */}
      <section className="relative overflow-hidden pt-12 lg:pt-20 pb-16 lg:pb-24">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-teal-100/30 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.22em] text-teal-600 font-semibold mb-3">
              Our Specialists
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-slate-900">
              Doctors who turn dental visits into <span className="text-teal-700 font-medium">good experiences.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Lotus Family Dental was founded with one belief — that exceptional dental care should feel
              effortless, painless, and human. Meet the team that makes it happen.
            </p>
          </div>
        </div>
      </section>

      {/* Doctors detailed */}
      {DOCTORS.map((d, i) => (
        <section key={i} className={`py-16 lg:py-24 ${i % 2 === 1 ? "bg-slate-50" : ""}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`grid lg:grid-cols-12 gap-10 lg:gap-14 items-center ${i % 2 === 1 ? "lg:[direction:rtl]" : ""}`}>
              <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:[direction:ltr]" : ""}`}>
                <div className="reveal aspect-[4/5] rounded-[2rem] overflow-hidden bg-slate-100 shadow-2xl shadow-slate-300/30">
                  <img src={d.image} alt={d.name} className="w-full h-full object-cover object-top" />
                </div>
              </div>
              <div className={`lg:col-span-7 reveal ${i % 2 === 1 ? "lg:[direction:ltr]" : ""}`}>
                <div className="text-xs uppercase tracking-[0.22em] text-teal-600 font-semibold mb-3">
                  {d.specialty}
                </div>
                <h2 className="font-heading text-3xl lg:text-5xl font-light tracking-tight text-slate-900">
                  {d.name}
                </h2>
                <p className="mt-3 text-slate-500 font-medium">{d.qualifications}</p>
                <p className="mt-6 text-lg text-slate-600 leading-relaxed">{d.bio}</p>

                <ul className="mt-8 space-y-3">
                  {d.titles.map((t, k) => (
                    <li key={k} className="flex items-start gap-3 text-slate-700">
                      <Award className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => setDialogOpen(true)}
                  className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-teal-600 text-white font-medium hover:bg-teal-700 transition"
                  data-testid={`book-with-doctor-${i}`}
                >
                  Book with {d.name.split(" ")[0]} {d.name.split(" ")[1]} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Values / Story */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="reveal">
              <div className="text-xs uppercase tracking-[0.22em] text-teal-600 font-semibold mb-3">
                Our promise
              </div>
              <h2 className="font-heading text-3xl lg:text-5xl font-light tracking-tight text-slate-900">
                Built on <span className="text-teal-700 font-medium">trust, science & gentleness.</span>
              </h2>
              <p className="mt-5 text-lg text-slate-600">
                Lotus Family Dental was born to deliver a calmer, smarter dental experience to families in
                Prayagraj. From the way our chairs are arranged to the words we choose — every detail is
                designed to make you feel at ease.
              </p>
              <ul className="mt-8 space-y-4">
                {[
                  { t: "Honesty in pricing", d: "Written treatment plan & quote before we begin." },
                  { t: "Science-backed care", d: "Latest protocols from MDS specialists with years of clinical research." },
                  { t: "Gentle dentistry", d: "Painless techniques and a warm, judgement-free environment." },
                ].map((v, k) => (
                  <li key={k} className="flex gap-4">
                    <div className="w-10 h-10 rounded-2xl bg-teal-50 text-teal-700 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">{v.t}</div>
                      <div className="text-sm text-slate-600 mt-0.5">{v.d}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4 reveal">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-slate-100">
                <img src={ASSETS.clinicWall} alt="Clinic wall" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-slate-100 mt-8">
                <img src={ASSETS.modernRoom1} alt="Modern dental room" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-slate-100 -mt-4">
                <img src={ASSETS.clinicEquipment} alt="Equipment" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-slate-100">
                <img src={ASSETS.modernRoom2} alt="Modern room" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <AppointmentDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </>
  );
}
